/**
 * @file PipelineEditDialog.tsx
 * @description Создание / редактирование воронки (phase09; phase19.31 —
 * флажки на общем `Checkbox` в строке 48px вместо нативных).
 */

import { useEffect, useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useNotify } from "../ui/NotifyProvider";
import { SelectField } from "../ui/SelectField";
import { ApiError } from "../../lib/api-client";
import {
  PIPELINE_KIND_LABEL,
  createPipeline,
  updatePipeline,
  type PipelineDto,
  type PipelineKind,
} from "../../lib/api-crm";

interface Props {
  mode: "create" | "edit";
  pipeline?: PipelineDto;
  onClose: () => void;
  onSaved: (p: PipelineDto) => void;
}

const KINDS: PipelineKind[] = ["SALES_B2C", "SALES_B2B", "RETURNS", "RECLAMATION", "CUSTOM"];

/** Строка флажка: общий `Checkbox`, цель касания 48px. */
const CHECK_ROW = "type-body flex min-h-12 cursor-pointer items-center gap-2 text-on-surface";

export function PipelineEditDialog({ mode, pipeline, onClose, onSaved }: Props) {
  const notify = useNotify();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<PipelineKind>("SALES_B2C");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    if (mode === "edit" && pipeline) {
      setName(pipeline.name);
      setKind(pipeline.kind);
      setDescription(pipeline.description ?? "");
      setIsDefault(pipeline.isDefault);
      setIsActive(pipeline.isActive);
    } else {
      setName("");
      setKind("SALES_B2C");
      setDescription("");
      setIsDefault(false);
      setIsActive(true);
    }
  }, [mode, pipeline]);

  async function onSubmit() {
    setError(null);
    if (!name.trim()) {
      setError("Название обязательно.");
      return;
    }
    setSaving(true);
    try {
      const saved =
        mode === "create"
          ? await createPipeline({
              name: name.trim(),
              kind,
              description: description.trim() || undefined,
              isDefault,
            })
          : await updatePipeline(pipeline!.id, {
              name: name.trim(),
              description: description.trim() || undefined,
              isDefault,
              isActive,
            });
      notify.success(mode === "create" ? "Воронка создана" : "Воронка обновлена");
      onSaved(saved);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Не удалось сохранить";
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Новая воронка" : "Редактирование воронки"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {error ? <Alert severity="error">{error}</Alert> : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pp-name">Название</Label>
            <Input
              id="pp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Розничные продажи"
              autoFocus
            />
          </div>

          {mode === "create" ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pp-kind">Тип воронки</Label>
              <SelectField
                id="pp-kind"
                ariaLabel="Тип воронки"
                value={kind}
                onChange={(value) => setKind(value as PipelineKind)}
                options={KINDS.map((k) => ({ value: k, label: PIPELINE_KIND_LABEL[k] }))}
              />
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pp-desc">Описание</Label>
            <Input
              id="pp-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Краткое описание воронки"
            />
          </div>

          <label className={CHECK_ROW}>
            <Checkbox checked={isDefault} onCheckedChange={(c) => setIsDefault(c === true)} />
            По умолчанию для новых сделок этого типа
          </label>

          {mode === "edit" ? (
            <label className={CHECK_ROW}>
              <Checkbox checked={isActive} onCheckedChange={(c) => setIsActive(c === true)} />
              Активна
            </label>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? "Сохранение..." : "Сохранить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
